import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { useCurrentUser } from '../auth';
import { ACCOUNTS_GRID_KEY } from '../../types/user';
import {
  ACCOUNTS_GRID_PAGE_SIZE,
  type AccountGridFilters,
  type AccountGridQuery,
  type AccountSortKey,
  type SortDirection,
} from '../../types/accountGrid';
import { bulkRefreshConnections, getAccountsGrid, listAccounts, listUsers } from './api';
import {
  ACCOUNTS_COLUMNS,
  accountColumnDef,
  type AccountColumnKey,
} from './accountColumns';
import { renderAccountCell } from './accountCell';
import { AccountsViewMenu, initialAccountVisibleFromPrefs } from './AccountsViewMenu';
import { AccountsBulkAssignModal } from './AccountsBulkAssignModal';
import { AccountsBulkReportingSyncModal } from './AccountsBulkReportingSyncModal';
import { AccountsFilterBar } from './AccountsFilterBar';

// Debounce a value by `delayMs` (search input -> query). 250ms coalesces a
// typist's keystrokes into one fetch.
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function AccountsGrid() {
  const user = useCurrentUser();
  const qc = useQueryClient();
  const isAdmin = user.role === 'admin';

  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<AccountGridFilters>({});
  // Search: seeded from the URL `q` param (shareable/bookmarkable), debounced
  // into the query. Trimmed -> whitespace-only behaves as empty (no filter).
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') ?? '');
  const search = useDebounced(searchInput.trim(), 250);
  const [sortKey, setSortKey] = useState<AccountSortKey>('name');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [visible, setVisible] = useState<Set<AccountColumnKey>>(() =>
    initialAccountVisibleFromPrefs(user.gridPreferences?.[ACCOUNTS_GRID_KEY]),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reportingSyncOpen, setReportingSyncOpen] = useState(false);

  // Reset to page 1 whenever the filter/search/sort changes (the result set shifts).
  useEffect(() => {
    setPage(1);
  }, [filters, search, sortKey, sortDir]);

  // Persist the (debounced) search term in the URL so the filtered view is
  // shareable/bookmarkable. Replace (not push) so typing doesn't spam history.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (search) next.set('q', search);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const query: AccountGridQuery = useMemo(
    () => ({ filters, search, sortKey, sortDir, page, pageSize: ACCOUNTS_GRID_PAGE_SIZE }),
    [filters, search, sortKey, sortDir, page],
  );

  const grid = useQuery({
    queryKey: ['accounts-grid', query],
    queryFn: () => getAccountsGrid(query),
    placeholderData: (prev) => prev,
  });
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: () => listUsers() });
  // Brand options for the Brand filter — the small set of brand-type accounts.
  // Fetched separately (the grid endpoint returns only a page); cached + sorted.
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() });
  const brandOptions = useMemo(
    () =>
      (accountsQuery.data ?? [])
        .filter((a) => a.accountType === 'brand')
        .map((a) => ({ value: a.id, label: a.name }))
        .sort((x, y) => x.label.localeCompare(y.label)),
    [accountsQuery.data],
  );
  // Country options for the Country filter — the distinct authoritative
  // Account.country values present in the roster (§3a; free text, so derived
  // from data rather than a fixed enum).
  const countryOptions = useMemo(
    () =>
      [...new Set((accountsQuery.data ?? []).map((a) => a.country).filter((c): c is string => !!c))]
        .sort((x, y) => x.localeCompare(y))
        .map((c) => ({ value: c, label: c })),
    [accountsQuery.data],
  );

  const cols = ACCOUNTS_COLUMNS.filter((c) => visible.has(c.key));
  const rows = grid.data?.rows ?? [];
  const total = grid.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / ACCOUNTS_GRID_PAGE_SIZE));

  const onSort = (key: AccountSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const pageIds = rows.map((r) => r.account.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const afterBulk = () => {
    setSelected(new Set());
    void qc.invalidateQueries({ queryKey: ['accounts-grid'] });
    void qc.invalidateQueries({ queryKey: ['accounts'] });
  };

  // Manual connection-status refresh (PART D). Page-scoped or selection-scoped;
  // re-reads stored booleans by invalidating the grid query on completion.
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const refreshMut = useMutation({
    mutationFn: (ids: string[]) => bulkRefreshConnections(ids),
    onSuccess: (r) => {
      setRefreshMsg(
        `Refreshed ${r.refreshed}` +
          (r.skipped ? `, skipped ${r.skipped}` : '') +
          (r.failed ? `, failed ${r.failed}` : ''),
      );
      void qc.invalidateQueries({ queryKey: ['accounts-grid'] });
    },
    onError: () => setRefreshMsg('Refresh failed. Try again.'),
  });
  const refreshIds = (ids: string[]) => {
    if (ids.length === 0 || refreshMut.isPending) return;
    setRefreshMsg(null);
    refreshMut.mutate(ids);
  };

  if (grid.isLoading) return <CenteredSpinner />;
  if (grid.error) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load accounts: {(grid.error as Error).message}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <div className="relative max-w-md">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search accounts by name (incl. parent group / brand)…"
            aria-label="Search accounts"
            className="w-full rounded border border-slate-300 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
          {searchInput !== '' && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-lg leading-none"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mb-3">
        <AccountsFilterBar
          filters={filters}
          onChange={setFilters}
          users={usersQuery.data ?? []}
          brandOptions={brandOptions}
          countryOptions={countryOptions}
          currentUserUid={user.uid}
        />
        <div className="flex items-center gap-2">
          {refreshMsg && <span className="text-xs text-slate-500">{refreshMsg}</span>}
          <Button
            variant="outline"
            onClick={() => refreshIds(pageIds)}
            disabled={refreshMut.isPending || pageIds.length === 0}
            title="Re-check Zernio connection status for the accounts on this page"
          >
            {refreshMut.isPending ? 'Refreshing…' : 'Refresh status'}
          </Button>
          <AccountsViewMenu currentUser={user} visibleColumns={visible} onApply={setVisible} />
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  aria-label="Select all on page"
                  checked={allOnPageSelected}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        !allOnPageSelected && pageIds.some((id) => selected.has(id));
                    }
                  }}
                  onChange={toggleAllOnPage}
                />
              </th>
              {cols.map((c) => {
                const def = accountColumnDef(c.key);
                const active = def.sortKey && def.sortKey === sortKey;
                return (
                  <th key={c.key} className="px-3 py-3 font-medium whitespace-nowrap">
                    {def.sortKey ? (
                      <button
                        type="button"
                        onClick={() => onSort(def.sortKey!)}
                        className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? 'text-slate-900 font-semibold' : 'text-slate-500'}`}
                      >
                        {def.label}
                        <span aria-hidden className="text-[10px]">
                          {active ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                        </span>
                      </button>
                    ) : (
                      def.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={cols.length + 1} className="px-4 py-8 text-center text-sm text-slate-500">
                  No accounts match these filters.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const isSel = selected.has(row.account.id);
                return (
                  <tr
                    key={row.account.id}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${isSel ? 'bg-indigo-50/50' : ''}`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.account.name}`}
                        checked={isSel}
                        onChange={() => toggleRow(row.account.id)}
                      />
                    </td>
                    {cols.map((c) => (
                      <td key={c.key} className="px-3 py-2 align-top">
                        {renderAccountCell(row, c.key)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-sm text-slate-600">
        <span>
          {total} account{total === 1 ? '' : 's'}
          {grid.isFetching && <span className="ml-2 text-slate-400">updating…</span>}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Previous
          </Button>
          <span>
            Page {grid.data?.page ?? page} of {pageCount}
          </span>
          <Button
            variant="outline"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="sticky bottom-4 mt-4 flex items-center justify-between gap-3 rounded-lg border border-slate-300 bg-white shadow-lg px-4 py-3">
          <span className="text-sm font-medium text-slate-700">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => refreshIds([...selected])}
              disabled={refreshMut.isPending}
              title="Re-check Zernio connection status for the selected accounts"
            >
              {refreshMut.isPending ? 'Refreshing…' : 'Refresh status'}
            </Button>
            <Button
              onClick={() => setBulkOpen(true)}
              disabled={!isAdmin}
              title={isAdmin ? undefined : 'Only admins can assign account managers'}
            >
              Assign Account Manager
            </Button>
            <Button
              variant="outline"
              onClick={() => setReportingSyncOpen(true)}
              disabled={!isAdmin}
              title={isAdmin ? undefined : 'Only admins can change reporting sync'}
            >
              Reporting sync
            </Button>
            <Button variant="outline" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {bulkOpen && (
        <AccountsBulkAssignModal
          accountIds={[...selected]}
          users={usersQuery.data ?? []}
          onClose={() => setBulkOpen(false)}
          onDone={afterBulk}
        />
      )}

      {reportingSyncOpen && (
        <AccountsBulkReportingSyncModal
          accountIds={[...selected]}
          onClose={() => setReportingSyncOpen(false)}
          onDone={afterBulk}
        />
      )}
    </div>
  );
}
